import { AlertTriangle, Check, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useApplyJudgeTuningRun, useCallJudgeTuning } from '@/hooks/useJudgeTuning';

import type { JudgeTuningRun } from '@/lib/api/judge-tuning.api';

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  QUEUED: 'secondary',
  RUNNING: 'default',
  SUCCEEDED: 'default',
  PARTIAL: 'outline',
  FAILED: 'destructive',
  CANCELLED: 'outline',
  DRAFT: 'outline',
};

const fmtRate = (v: number | undefined) => (v !== undefined ? `${(v * 100).toFixed(0)}%` : '--');

const RunRow = ({ run }: { run: JudgeTuningRun }) => {
  const apply = useApplyJudgeTuningRun();
  const summary = run.result_summary;
  const isRunning = run.status === 'QUEUED' || run.status === 'RUNNING';
  const configResultValues = Object.values(run.config_results ?? {});
  const hasCandidate = configResultValues.some((r) => r.candidatePrompt && !r.applied);
  const hasUnmappedNotes = configResultValues.some(
    (r) => r.unmappedNoteCallIds && r.unmappedNoteCallIds.length > 0,
  );
  const hasDerivedCorrections = configResultValues.some((r) => r.usedDerivedCorrections);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-gray-50/50 px-2.5 py-1.5 text-xs dark:bg-gray-900/30">
      <Badge variant={STATUS_VARIANT[run.status] ?? 'outline'} className="text-[10px]">
        {isRunning && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        {run.status}
      </Badge>

      {summary?.baselinePassRate !== undefined && (
        <span className="text-muted-foreground">
          {fmtRate(summary.baselinePassRate)} &rarr; {fmtRate(summary.bestPassRate)}
        </span>
      )}

      <Link
        to={`/agent-improvement/judge-tuning/${run.id}`}
        className="text-primary ml-auto inline-flex items-center gap-1 hover:underline"
      >
        View run <ExternalLink className="h-3 w-3" />
      </Link>

      {hasCandidate && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 text-[10px]"
          onClick={() => apply.mutate({ id: run.id })}
          disabled={apply.isPending}
        >
          {apply.isPending ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : (
            <Check className="mr-1 h-3 w-3" />
          )}
          Apply
        </Button>
      )}

      {hasDerivedCorrections && (
        <span className="text-muted-foreground inline-flex items-center gap-1 text-[10px]">
          <Sparkles className="h-3 w-3" />
          inferred from note
        </span>
      )}

      {hasUnmappedNotes && (
        <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          note could not be mapped to a field
        </span>
      )}
    </div>
  );
};

interface JudgeTuningInlineStatusProps {
  callId: string;
  /** Filter runs by target type so each card only shows its own runs. */
  targetType?: string;
}

/**
 * Shows judge-tuning run status inline on the call page, under a judge card.
 * Polls while any run is QUEUED or RUNNING.
 */
export const JudgeTuningInlineStatus = ({ callId, targetType }: JudgeTuningInlineStatusProps) => {
  const { data: runs } = useCallJudgeTuning(callId);

  const filtered = targetType
    ? (runs ?? []).filter((r) => r.target_type === targetType)
    : (runs ?? []);

  if (filtered.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
        Tuning runs
      </div>
      {filtered.map((run) => (
        <RunRow key={run.id} run={run} />
      ))}
    </div>
  );
};
